import { useEffect, useState } from "react"
import styled from "styled-components"

import { Skeleton, Text } from "@minuthq/meatball-ui-react"

import { EmptyStateWithImage } from "src/components/EmptyState/EmptyStateWithImage"
import NoMessages from "src/components/EmptyState/images/no-messages.svg"
import { GuestConversationNavItem } from "src/components/GuestInbox/GuestConversations/GuestConversationNavItem"
import {
  GuestConversationStatusSelect,
  TConversationStatusSelectValue,
} from "src/components/GuestInbox/GuestConversations/GuestConversationStatusSelect"
import { areaPaddingSize } from "src/components/GuestInbox/guestInboxSharedStyles"
import { ScrollArea } from "src/components/ScrollArea/ScrollArea"
import { useFetchOrganizationGuestsInfinite } from "src/data/guests/queries/guestQueries"
import { TConversationStatus } from "src/data/guests/types/guestTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { spacing } from "src/ui/spacing"

export function GuestConversationsList({
  initilizeToFirstConversation = false,
}: {
  initilizeToFirstConversation?: boolean
}) {
  const { tPlaceholder } = useTranslate()

  const { navigate } = useRouter()
  const { org } = useOrganization()

  const [conversationStatus, setConversationStatus] =
    useState<TConversationStatusSelectValue>("all")

  const conversationStatusFilter: TConversationStatus[] =
    conversationStatus === "all"
      ? ["unresolved", "resolved"]
      : [conversationStatus]

  const fetchOrganizationGuests = useFetchOrganizationGuestsInfinite({
    orgId: org.id,
    filters: {
      conversation_statuses: conversationStatusFilter,
      include_anonymized: false,
      sort_by: "last_message_created_at",
      sort_order: "desc",
      limit: 20,
    },
    options: {
      refetchInterval: 15000,
    },
  })

  /**
   * If `initilizeToFirstConversation` is true, we navigate to the first conversation
   */
  useEffect(() => {
    if (initilizeToFirstConversation && fetchOrganizationGuests.data) {
      const firstGuest = fetchOrganizationGuests.data.pages[0]?.guests[0]

      if (firstGuest) {
        navigate(Routes.InboxConversation.location(firstGuest.guest_id))
      }
    }
  }, [initilizeToFirstConversation, fetchOrganizationGuests.data, navigate])

  if (fetchOrganizationGuests.isLoading) {
    return (
      <Container>
        <Header>
          <Text variant="heading3">{tPlaceholder("Inbox")}</Text>
        </Header>

        <ListWrapper>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} variant="rect" height={60} />
          ))}
        </ListWrapper>
      </Container>
    )
  }

  if (fetchOrganizationGuests.isError) {
    throw new Error("Could not fetch organization guests")
  }

  const guests = fetchOrganizationGuests.data.pages.flatMap(
    (page) => page.guests
  )

  return (
    <Container>
      <Header>
        <Text variant="heading3" marginBottom={spacing.S}>
          {tPlaceholder("Inbox")}
        </Text>

        <GuestConversationStatusSelect
          selectedValue={conversationStatus}
          onChange={setConversationStatus}
        />
      </Header>

      <ScrollArea
        onScrollToBottom={() => {
          void fetchOrganizationGuests.fetchNextPage()
        }}
        scrollBottomThreshold={100}
      >
        <ListWrapper>
          {guests.length > 0 ? (
            guests.map((guest) => (
              <GuestConversationNavItem
                key={guest.guest_id}
                appHref={
                  Routes.InboxConversation.location(guest.guest_id).pathname
                }
                title={guest.name}
                summary={guest.conversation?.last_message_content ?? ""}
                conversationStatus={guest.conversation?.status}
                lastSentMessageAt={guest.conversation?.last_message_created_at}
              />
            ))
          ) : (
            <GuestConversationsListEmpty status={conversationStatus} />
          )}

          {fetchOrganizationGuests.isFetchingNextPage && (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} variant="rect" height={30} />
              ))}
            </>
          )}
        </ListWrapper>
      </ScrollArea>
    </Container>
  )
}

function GuestConversationsListEmpty({
  status,
}: {
  status: TConversationStatusSelectValue
}) {
  const { tPlaceholder } = useTranslate()

  if (status === "resolved") {
    return (
      <EmptyStateWithImage
        Image={NoMessages}
        title={tPlaceholder("No resolved messages yet")}
        description={tPlaceholder(
          "This is where messages go after they've been handled. Once Autopilot replies or a team member marks them as resolved, you'll see them here."
        )}
        imageSpacing={spacing.XL}
      />
    )
  }

  if (status === "unresolved") {
    return (
      <EmptyStateWithImage
        Image={NoMessages}
        title={tPlaceholder("No unresolved messages")}
        description={tPlaceholder(
          "There's nothing needing your attention right now. Messages that Autopilot can't resolve or escalate will show up here."
        )}
        imageSpacing={spacing.XL}
      />
    )
  }

  return (
    <EmptyStateWithImage
      Image={NoMessages}
      title={tPlaceholder("No messages yet")}
      description={tPlaceholder(
        "Guest messages will appear here. Autopilot will answer common questions and escalate anything it can’t handle or if no Knowledge has been added yet."
      )}
      imageSpacing={spacing.XL}
    />
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`

const Header = styled.div`
  padding: ${areaPaddingSize};
`

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.XS2};
  padding: 0 ${areaPaddingSize} ${areaPaddingSize} ${areaPaddingSize};
`
